import React, { useEffect, useState, useRef } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import styled from '@emotion/styled';
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';

import { BoxShadow } from '../../../../shared/GlobalStyles';
import Button from '../../../../shared/_TBComponents/Button';
import DatePicker from '../../../../shared/_TBComponents/DatePicker';
import DropDown from '../../../force-plate-dashboard/components/presentational/ForcePlateDropDown';
import AssignedCompletedChart from './AssignedCompletedChart';
import ExerciseFilter from './ExerciseFilter';
import { dashboardTracker } from '../../../../shared/utils/amplitudeHelper';

import {
  setGroup,
  setSelectedAthlete,
  fetchAthletesFromGroup,
  setSelectedStartDate,
  setStartDateData,
  setSelectedEndDate,
  setEndDateData,
  fetchVTDashData,
  setSaveVTDashModal,
} from '../../ducks/volumeTrackerDashboardActions';

const DashboardContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: fit-content;
  margin: 15px 15px 7px 15px;
  background-color: #FFFFFF;
  border-radius: 10px;
  padding: 16px;
  box-shadow: ${BoxShadow};
  position: relative;

  @media print {
    @page {
      size: A4 landscape;
      margin: 6px;
    }
    width: 100% !important;
    max-width: 100% !important;
  }
`;

const PrintOnlyHeader = styled('div')`
  display: none;

  @media print {
    display: block;
    padding: 16px 8px;
    font-family: 'Nunito Sans';
    font-size: 24px;
    font-weight: 300;
  }
`;

const OptionsHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 26px;
`;

const MenuOptionsContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
`;

const DateOptionsContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
  margin-right: 10px;
`;

const FilterWrapper = styled('div')`
  display: flex;
  margin: 5px 0;
`;

const IconContainer = styled('div')`
  display: flex;
  margin: 5px 0;
`;

const PrintWrapper = styled('div')`
  @media print {
    margin-top: 10px;
    margin-right: 10px;
    background: white;
    page-break-after: always;
  }
`;

const NoDataModal = styled('div')`
  position: absolute;
  text-align: center;
  top: 50%;
  left: 50%;
  margin: -45px 0px 0px -182px;
  height: 119px;
  width: 400px;
  background-color: #FFFFFF;
  border-radius: 8px;
  box-shadow: ${BoxShadow};
  font-family: 'Nunito Sans';
  font-size: 19px;
  color: #424242;
  padding: 15px;
`;

const ModalTitle = styled('div')`
  font-weight: bold;
`;

const ModalMessage = styled('div')`
  font-weight: 300;
`;

const AssignedCompletedTab = () => {
  const dispatch = useDispatch();
  const printRef = useRef();

  const [readyPrint, setReadyPrint] = useState(false);

  const currentUser = useSelector(
    (state) => state.auth.data.currentUser,
  );

  const activeTemplate = useSelector(
    (state) => state.volumeTrackerDashboard.data.activeTemplate,
  );
  const athleteList = useSelector(
    (state) => state.volumeTrackerDashboard.data.athleteList,
  );
  const bucketSize = useSelector(
    (state) => state.volumeTrackerDashboard.data.bucketSize,
  );
  const endDateData = useSelector(
    (state) => state.volumeTrackerDashboard.data.endDateData,
  );
  const exerciseFilters = useSelector(
    (state) => state.volumeTrackerDashboard.data.exerciseFilters,
  );
  const group = useSelector(
    (state) => state.volumeTrackerDashboard.data.group,
  );
  const groups = useSelector(
    (state) => state.volumeTrackerDashboard.data.groups,
  );
  const rangeMax = useSelector(
    (state) => state.volumeTrackerDashboard.data.assignedCompletedRangeMax,
  );
  const rangeMin = useSelector(
    (state) => state.volumeTrackerDashboard.data.assignedCompletedRangeMin,
  );
  const selectedAthlete = useSelector(
    (state) => state.volumeTrackerDashboard.data.selectedAthlete,
  );
  const selectedStartDate = useSelector(
    (state) => state.volumeTrackerDashboard.data.selectedStartDate,
  );
  const selectedEndDate = useSelector(
    (state) => state.volumeTrackerDashboard.data.selectedEndDate,
  );
  const startDateData = useSelector(
    (state) => state.volumeTrackerDashboard.data.startDateData,
  );
  const vtDashData = useSelector(
    (state) => state.volumeTrackerDashboard.data.vtDashData,
  );

  const isVTDashDataLoading = useSelector(
    (state) => state.volumeTrackerDashboard.ui.isVTDashDataLoading,
  );

  const hasChartData = !!vtDashData?.length;

  const athleteDropdownList = [
    { fullName: 'View Group', userId: null },
    ...[...athleteList]
      .sort((a, b) => a.last.localeCompare(b.last))
      .map((athlete) => ({
        fullName: `${athlete.first} ${athlete.last}`,
        userId: athlete.id,
        ...athlete,
      })),
  ];

  const athletes = group
    ? athleteDropdownList
    : [{ id: 'fake-id', fullName: 'Select a group first', fake: true }];

  const exerciseIds = exerciseFilters?.length > 0
    ? exerciseFilters.map((item) => item.id)
    : [];

  const getBackendRangeMax = () => rangeMax - bucketSize;

  const fetchData = useDebouncedCallback(() => {
    if (!group?.id || !startDateData || !endDateData) return;

    dispatch(
      fetchVTDashData(
        currentUser.accountCode,
        startDateData,
        endDateData,
        group.id,
        exerciseFilters.map((e) => e.id),
        bucketSize,
        getBackendRangeMax(),
        rangeMin,
      ),
    );
  }, 100);

  useEffect(() => {
    fetchData();
  }, [
    exerciseFilters,
    rangeMin,
    rangeMax,
    bucketSize,
    startDateData,
    endDateData,
    group?.id,
    fetchData,
  ]);

  // Ensure startDateData and endDateData strings update on initial load or date change
  useEffect(() => {
    if (selectedStartDate && selectedEndDate) {
      dispatch(setStartDateData(moment(selectedStartDate).format('YYYY-MM-DD')));
      dispatch(setEndDateData(moment(selectedEndDate).format('YYYY-MM-DD')));
    }
  }, [dispatch, selectedStartDate, selectedEndDate]);

  const handleStartDate = (date) => {
    const start = moment(date);
    let end = moment(selectedEndDate);
    // Update startDate if endDate is before or if it is beyond 8 weeks
    if (!end.isValid() || end.isBefore(start) || end.diff(start, 'days') >= 56) {
      end = moment.min(start.clone().add(55, 'days'), moment());
      dispatch(setSelectedEndDate(end));
      dispatch(setEndDateData(end.format('YYYY-MM-DD')));
    }
    // Amplitude tracking
    dashboardTracker('Volume Tracker', 'Assigned/Completed - Date Picker', 'Start date selected');
    batch(() => {
      dispatch(setStartDateData(start.format('YYYY-MM-DD')));
      dispatch(setSelectedStartDate(start));
      if (group?.id) {
        dispatch(
          fetchVTDashData(
            currentUser.accountCode,
            start.format('YYYY-MM-DD'),
            end.format('YYYY-MM-DD'),
            group?.id,
            exerciseIds,
            bucketSize,
            getBackendRangeMax(),
            rangeMin,
          ),
        );
      }
    });
  };

  const handleEndDate = (date) => {
    let start = moment(selectedStartDate);
    const end = moment(date);
    if (end.isBefore(start) || end.diff(start, 'days') >= 56) {
      start = moment.min(end.clone().subtract(55, 'days'), moment());
      dispatch(setSelectedStartDate(start));
      dispatch(setStartDateData(start.format('YYYY-MM-DD')));
    }
    // Amplitude tracking
    dashboardTracker('Volume Tracker', 'Assigned/Completed - Date Picker', 'End date selected');
    batch(() => {
      dispatch(setEndDateData(end.format('YYYY-MM-DD')));
      dispatch(setSelectedEndDate(end));
      if (group?.id) {
        dispatch(
          fetchVTDashData(
            currentUser.accountCode,
            start.format('YYYY-MM-DD'),
            end.format('YYYY-MM-DD'),
            group?.id,
            exerciseIds,
            bucketSize,
            getBackendRangeMax(),
            rangeMin,
          ),
        );
      }
    });
  };

  const setSelectedGroup = (option) => {
    // Amplitude tracking
    dashboardTracker('Volume Tracker', 'Assigned/Completed - Group Picker', 'Group selected');
    batch(() => {
      dispatch(setGroup(option));
      dispatch(setSelectedAthlete(null, false));
      dispatch(fetchAthletesFromGroup(currentUser.accountCode, option.id));
      dispatch(
        fetchVTDashData(
          currentUser.accountCode,
          startDateData,
          endDateData,
          option.id,
          exerciseIds,
          bucketSize,
          getBackendRangeMax(),
          rangeMin,
        ),
      );
    });
  };

  const setAthlete = (option) => {
    if (option.fullName === 'View Group') {
      // Amplitude tracking
      dashboardTracker('Volume Tracker', 'Assigned/Completed - Athlete Picker', 'Athlete selection reset');
      dispatch(setSelectedAthlete(null, false));
    } else {
      // Amplitude tracking
      dashboardTracker('Volume Tracker', 'Assigned/Completed - Athlete Picker', 'Athlete selected');
      dispatch(setSelectedAthlete(option, true));
    }
  };

  const handleReactPrint = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => {
      setReadyPrint(false);
    },
  });

  const handlePrint = () => {
    setReadyPrint(true);
  };

  useEffect(() => {
    if (readyPrint) {
      setTimeout(() => {
        handleReactPrint();
      }, 1500);
    }
  }, [readyPrint]);

  return (
    <DashboardContainer ref={printRef} readyPrint={readyPrint}>
      {readyPrint && (
        <PrintOnlyHeader>
          Volume Tracker
        </PrintOnlyHeader>
      )}
      <OptionsHeader>
        <MenuOptionsContainer>
          <DropDown
            list={groups}
            label='name'
            selectItemFunction={setSelectedGroup}
            headerWidthPx={175}
            placeholder='Select a Group'
            defaultOption={group || {}}
            readOnly
          />
          <DropDown
            list={athletes}
            label='fullName'
            selectItemFunction={setAthlete}
            headerWidthPx={175}
            placeholder='Select an Athlete'
            defaultOption={selectedAthlete || {}}
            disabled={!group?.id}
            readOnly
          />
          <DateOptionsContainer>
            <DatePicker
              singleDate={false}
              startDate={selectedStartDate ? moment(selectedStartDate).toDate() : null}
              endDate={selectedEndDate ? moment(selectedEndDate).toDate() : null}
              setStartDate={handleStartDate}
              setEndDate={handleEndDate}
              maxDate={moment().toDate()}
              minDate={moment('1970-01-01').toDate()}
              styledWrapperProps={{ customBorder: '1px solid #D8D8D8', customHeight: '42px' }}
            />
          </DateOptionsContainer>
          <FilterWrapper>
            {!readyPrint && <ExerciseFilter />}
          </FilterWrapper>
        </MenuOptionsContainer>
        {!readyPrint && (
          <IconContainer>
            {!activeTemplate && (
              <Button
                className='saveBtn'
                iconOnly
                iconSize={16}
                icon='save'
                styledWrapperProps={{
                  padding: '11px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #D8D8D8',
                }}
                onClick={() => {
                  dispatch(setSaveVTDashModal(true));
                  // KPI tracking
                  dashboardTracker(
                    'Volume Tracker',
                    'View Only Overview - Save',
                    'Save button clicked',
                  );
                }}
              />
            )}
            <Button
              disabled={!hasChartData}
              iconOnly
              iconSize={16}
              icon='printer'
              styledWrapperProps={{
                padding: '11px',
                marginLeft: '8px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #D8D8D8',
              }}
              onClick={() => {
                handlePrint();
                // KPI tracking
                dashboardTracker(
                  'Volume Tracker',
                  'View Only Overview - Print',
                  'Print button clicked',
                );
              }}
            />
          </IconContainer>
        )}
      </OptionsHeader>
      {exerciseFilters?.length > 0 && group?.id ? (
        exerciseFilters.map((filter) => {
          const matchingExerciseData = vtDashData?.find((e) => e.exerciseName === filter.name);
          return (
            <PrintWrapper key={filter.name}>
              <AssignedCompletedChart
                key={filter.name}
                exerciseData={matchingExerciseData || { exerciseName: filter.name, dates: [] }}
                bucketSize={bucketSize}
                rangeMax={rangeMax}
                rangeMin={rangeMin}
                selectedAthlete={selectedAthlete}
                isLoading={isVTDashDataLoading}
                readyPrint={readyPrint}
              />
            </PrintWrapper>
          );
        })
      ) : (
        <AssignedCompletedChart
          exerciseData={null}
          bucketSize={bucketSize}
          rangeMax={rangeMax}
          rangeMin={rangeMin}
          selectedAthlete={selectedAthlete}
          isLoading={isVTDashDataLoading}
        />
      )}
      {!isVTDashDataLoading && (
        !group || (exerciseFilters.length === 0 && !hasChartData)
      ) && (
        <NoDataModal>
          <ModalTitle>
            {!group ? 'Select a Group' : 'No Data Available'}
          </ModalTitle>
          <ModalMessage>
            {!group
              ? 'Select a group using the dropdown above to get started'
              : 'There is no data available. Please select an exercise to get started.'}
          </ModalMessage>
        </NoDataModal>
      )}
    </DashboardContainer>
  );
};

export default AssignedCompletedTab;
